import { useState } from 'react';
import Link from 'next/link';
import { Controller } from 'react-hook-form';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { Button } from '@mildang/design-system/Button';
import { AdornmentButton } from '@mildang/design-system/AdornmentButton';
import { InputFieldBase } from '@mildang/design-system/InputField';
import { Image } from '@mildang/design-system/Image';
import { css } from '@mildang/styled-system/css';
import { HStack, VStack } from '@mildang/styled-system/jsx';
import Eye from '@mildang/icons/react/eye';
import EyeOff from '@mildang/icons/react/eye-off';
import LoginLayout from '@ui/components/user/screens/LoginScreen/LoginLayout';
import { heroImages } from '@ui/components/user/screens/LoginScreen/heroImages';
import { publicServiceBrand } from '@ui/components/patterns/ServiceBrand/serviceBrand';
import logo from '../../../public/images/signup/logo.svg';
import { useLoginForm } from './LoginForm';
import FindEmailDialog from './FindEmailDialog';
import ResetPasswordDialog from './ResetPasswordDialog';

// 비밀번호 표시 버튼이 있어도 두 입력창 높이를 시안의 48px로 맞춘다.
const inputStyle = css({ minHeight: 'token(spacing.48)', py: '8' });

export default function StudyLoginScreen() {
  const { _ } = useLingui();
  const [showPassword, setShowPassword] = useState(false);
  const [dialog, setDialog] = useState<'findEmail' | 'resetPassword'>();
  const {
    control,
    formState: { errors, isSubmitting },
    onSubmit,
  } = useLoginForm();

  return (
    <LoginLayout
      service={publicServiceBrand?.service ?? 'study'}
      heroImage={heroImages.study}
      heroLogo={publicServiceBrand?.logoWhite}
      logo={
        publicServiceBrand ? (
          <Image
            src={publicServiceBrand.logo.src}
            alt={publicServiceBrand.name}
            draggable={false}
            width={publicServiceBrand.logo.width}
            height={publicServiceBrand.logo.height}
            unoptimized
          />
        ) : (
          <Image src={logo} alt="밀당PT Study" draggable={false} width={111.072} height={28.289} unoptimized />
        )
      }
      title={<Trans>1:1 온라인 퍼스널 티칭</Trans>}
      description={<Trans>내신 & 수능, 맞춤 수업과 학습 관리를 경험해보세요</Trans>}
    >
      <VStack alignItems="stretch" gap="24">
        <form onSubmit={onSubmit} noValidate>
          <VStack alignItems="stretch" gap={{ base: '24', sm: '32' }}>
            <VStack alignItems="stretch" gap="20">
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <InputFieldBase
                    {...field}
                    id="email"
                    label={<Trans>이메일</Trans>}
                    placeholder={_(msg`이메일 주소 입력...`)}
                    type="email"
                    autoComplete="email"
                    autoFocus
                    required
                    size="xl"
                    fullWidth
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    inputContainerClassName={inputStyle}
                  />
                )}
              />
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <InputFieldBase
                    {...field}
                    id="current-password"
                    label={<Trans>비밀번호</Trans>}
                    placeholder={_(msg`비밀번호 입력...`)}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    size="xl"
                    fullWidth
                    error={Boolean(errors.password)}
                    helperText={errors.password?.message}
                    inputContainerClassName={inputStyle}
                    endAdornment={
                      <AdornmentButton
                        aria-label={showPassword ? _(msg`비밀번호 숨기기`) : _(msg`비밀번호 표시`)}
                        aria-pressed={showPassword}
                        onClick={() => setShowPassword((value) => !value)}
                      >
                        {showPassword ? <Eye /> : <EyeOff />}
                      </AdornmentButton>
                    }
                  />
                )}
              />
            </VStack>
            <Button type="submit" variant="primary" size="xl" fullWidth loading={isSubmitting}>
              <Trans>로그인</Trans>
            </Button>
          </VStack>
        </form>
        <HStack gap="12" justifyContent="center" flexWrap="wrap">
          <Button
            variant="quaternary"
            size="lg"
            data-ga-name="findEmail_btn"
            onClick={() => setDialog('findEmail')}
          >
            <Trans>이메일 찾기</Trans>
          </Button>
          <Button
            variant="quaternary"
            size="lg"
            data-ga-name="resetPw_btn"
            onClick={() => setDialog('resetPassword')}
          >
            <Trans>비밀번호 재설정</Trans>
          </Button>
          <Button asChild variant="secondary" size="lg">
            <Link href="/signup" data-ga-on="click" data-ga-name="signup_btn">
              <Trans>회원가입</Trans>
            </Link>
          </Button>
        </HStack>
      </VStack>
      <FindEmailDialog
        key={`FindEmailDialog-${dialog === 'findEmail'}`}
        open={dialog === 'findEmail'}
        onClose={() => setDialog(undefined)}
      />
      <ResetPasswordDialog
        key={`ResetPasswordDialog-${dialog === 'resetPassword'}`}
        open={dialog === 'resetPassword'}
        onClose={() => setDialog(undefined)}
      />
    </LoginLayout>
  );
}
